import { useEffect, useState } from "react";

import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";
import { scopeTenant } from "../lib/tenantScope.mjs";

/**
 * Audit findings panel. Lists rows from public.audit_finding for the
 * current tenant, grouped by severity (HIGH first, then WARN, then INFO),
 * sorted by last_seen_date desc within each bucket.
 *
 * Surfaces the same data quality findings the `meridian refresh dws`
 * CLI shows, but inside the dashboard so the user doesn't have to drop
 * to the terminal. Companion to MER-144 (full insights surface) and
 * MER-146 (validate + audit layer); this delivers the audit half.
 */

interface AuditFinding {
  finding_id: string;
  severity: string;
  category: string;
  title: string;
  detail: string | null;
  affected_count: number | null;
  first_seen_date: string | null;
  last_seen_date: string | null;
  prior_severity: string | null;
  runs_seen: number | null;
  delta_status: string | null;
}

const SEVERITY_RANK: Record<string, number> = {
  HIGH: 0,
  WARN: 1,
  INFO: 2,
};

function severityColor(sev: string): { bg: string; fg: string } {
  switch (sev?.toUpperCase()) {
    case "HIGH":
      return { bg: "rgba(217, 75, 75, 0.12)", fg: "var(--flag-red)" };
    case "WARN":
      return { bg: "rgba(221, 160, 70, 0.15)", fg: "var(--flag-yellow)" };
    case "INFO":
      return { bg: "rgba(82, 119, 178, 0.12)", fg: "rgba(60, 90, 140, 1)" };
    default:
      return { bg: "var(--bg-card)", fg: "var(--text-muted)" };
  }
}

export default function AuditFindings() {
  const [state, setState] = useState<{
    findings: AuditFinding[];
    isLoading: boolean;
    error: string | null;
  }>({ findings: [], isLoading: true, error: null });
  const tenantId = useTenantId();

  useEffect(() => {
    if (!tenantId) return;
    const boundTenantId = tenantId;
    let cancelled = false;
    async function run() {
      const supabase = getSupabase();
      const { data, error } = await scopeTenant(
        supabase
          .from("audit_finding")
          .select(
            "finding_id, severity, category, title, detail, affected_count, first_seen_date, last_seen_date, prior_severity, runs_seen, delta_status",
          ),
        boundTenantId,
      )
        .order("last_seen_date", { ascending: false, nullsFirst: false })
        .limit(500);
      if (cancelled) return;
      if (error) {
        setState({ findings: [], isLoading: false, error: error.message });
        return;
      }
      const rows = (data ?? []) as AuditFinding[];
      rows.sort((a, b) => {
        const sa = SEVERITY_RANK[a.severity?.toUpperCase() ?? ""] ?? 99;
        const sb = SEVERITY_RANK[b.severity?.toUpperCase() ?? ""] ?? 99;
        if (sa !== sb) return sa - sb;
        const da = a.last_seen_date ?? "";
        const db = b.last_seen_date ?? "";
        return db.localeCompare(da);
      });
      setState({ findings: rows, isLoading: false, error: null });
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  return (
    <main style={{ padding: "24px 32px" }}>
      <header style={{ marginBottom: 24 }}>
        <div
          style={{
            fontSize: 12,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: "var(--text-muted)",
          }}
        >
          Data Quality
        </div>
        <h1 style={{ margin: "4px 0 0 0", fontSize: 22 }}>Findings</h1>
        <p style={{ marginTop: 8, fontSize: 13, color: "var(--text-muted)" }}>
          Pipeline-generated audit findings for this customer. Sorted by severity, then most recent.
        </p>
      </header>

      {state.isLoading ? (
        <div style={{ padding: 20, color: "var(--text-muted)" }}>Loading…</div>
      ) : state.error ? (
        <div style={{ padding: 20, color: "var(--flag-red)" }}>Error: {state.error}</div>
      ) : state.findings.length === 0 ? (
        <div
          style={{
            padding: "40px 20px",
            textAlign: "center",
            color: "var(--text-muted)",
            background: "var(--bg-card)",
            border: "1px dashed var(--border-subtle)",
            borderRadius: 12,
          }}
        >
          No audit findings for this customer.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {state.findings.map((f) => {
            const sev = (f.severity ?? "").toUpperCase();
            const color = severityColor(sev);
            return (
              <article
                key={f.finding_id}
                style={{
                  background: "var(--bg-card)",
                  border: "1px solid var(--border-subtle)",
                  borderRadius: 12,
                  padding: "16px 20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: 0.6,
                      padding: "3px 10px",
                      borderRadius: 999,
                      background: color.bg,
                      color: color.fg,
                      textTransform: "uppercase",
                    }}
                  >
                    {sev || "—"}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      color: "var(--text-muted)",
                    }}
                  >
                    {f.category}
                  </span>
                  {f.affected_count !== null && f.affected_count !== undefined ? (
                    <span style={{ fontSize: 11, color: "var(--text-muted)" }}>
                      {f.affected_count.toLocaleString()} affected
                    </span>
                  ) : null}
                  {f.last_seen_date ? (
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--text-muted)",
                        marginLeft: "auto",
                      }}
                    >
                      seen {f.last_seen_date}
                    </span>
                  ) : null}
                </div>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>{f.title}</div>
                {f.detail ? (
                  <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                    {f.detail}
                  </div>
                ) : null}
                {f.delta_status && f.delta_status !== "STABLE" ? (
                  <div style={{ fontSize: 11, marginTop: 8, color: color.fg }}>
                    {f.delta_status}
                    {f.prior_severity ? ` (was ${f.prior_severity})` : ""}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}
    </main>
  );
}
